import { useEffect, useRef, type ReactNode } from 'react';
import { IconChevronRight, IconClose } from './icons';

interface DetailPanelProps {
  children: ReactNode;
  collapsed?: boolean;
  onToggleCollapse?: () => void;
  mobileOpen?: boolean;
  onCloseMobile?: () => void;
  title?: string;
}

export function DetailPanel({
  children,
  collapsed = false,
  onToggleCollapse,
  mobileOpen = false,
  onCloseMobile,
  title = 'Detail',
}: DetailPanelProps) {
  const collapseBtnRef = useRef<HTMLButtonElement>(null);
  const expandBtnRef = useRef<HTMLButtonElement>(null);
  const wasCollapsed = useRef(collapsed);

  useEffect(() => {
    if (collapsed && !wasCollapsed.current) {
      expandBtnRef.current?.focus();
    } else if (!collapsed && wasCollapsed.current) {
      collapseBtnRef.current?.focus();
    }
    wasCollapsed.current = collapsed;
  }, [collapsed]);

  return (
    <>
      <aside
        className={`detail-panel${collapsed ? ' is-collapsed' : ''}`}
        data-testid="detail-panel"
        aria-label={title}
        hidden={collapsed}
      >
        <div className="side-panel__header">
          <h2 className="side-panel__title">{title}</h2>
          {onToggleCollapse && (
            <button
              ref={collapseBtnRef}
              type="button"
              className="icon-btn"
              aria-label={`Collapse ${title} panel`}
              data-testid="collapse-detail-panel"
              onClick={onToggleCollapse}
            >
              <IconChevronRight />
            </button>
          )}
        </div>
        <div className="side-panel__body">{children}</div>
      </aside>

      {collapsed && onToggleCollapse && (
        <button
          ref={expandBtnRef}
          type="button"
          className="panel-rail-btn panel-rail-btn--right"
          aria-label={`Expand ${title} panel`}
          data-testid="expand-detail-panel"
          onClick={onToggleCollapse}
        >
          {title}
        </button>
      )}

      {mobileOpen && (
        <div
          className="sheet-overlay"
          data-testid="detail-sheet"
          role="dialog"
          aria-modal="true"
          aria-label={title}
        >
          <div className="sheet-overlay__backdrop" onClick={onCloseMobile} />
          <div className="sheet-overlay__panel">
            <div className="side-panel__header">
              <h2 className="side-panel__title">{title}</h2>
              <button
                type="button"
                className="icon-btn"
                aria-label={`Close ${title}`}
                onClick={onCloseMobile}
              >
                <IconClose />
              </button>
            </div>
            <div className="side-panel__body">{children}</div>
          </div>
        </div>
      )}
    </>
  );
}
